import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import { toast } from "sonner";

import api, {
    formatApiError,
} from "@/lib/api";

import {
    useLang,
} from "@/contexts/LangContext";

import Layout from "@/components/Layout";

import {
    Button,
} from "@/components/ui/button";

import {
    Input,
} from "@/components/ui/input";


const refundableStatuses = new Set([
    "confirmed",
    "accepted",
    "preparing",
]);


export default function AdminOrders() {
    const { lang } = useLang();

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refundingId, setRefundingId] =
        useState(null);

    const [search, setSearch] =
        useState("");

    const [orderStatusFilter, setOrderStatusFilter] =
        useState("");

    const [paymentStatusFilter, setPaymentStatusFilter] =
        useState("");

    const [dateFrom, setDateFrom] =
        useState("");

    const [dateTo, setDateTo] =
        useState("");

    const [minAmount, setMinAmount] =
        useState("");

    const [maxAmount, setMaxAmount] =
        useState("");


    const load = useCallback(async () => {
        try {
            const response =
                await api.get(
                    "/admin/orders"
                );

            setOrders(
                response.data || []
            );
        } catch (error) {
            toast.error(
                formatApiError(
                    error.response
                        ?.data
                        ?.detail
                )
                || (
                    lang === "ar"
                        ? "\u062a\u0639\u0630\u0631 \u062a\u062d\u0645\u064a\u0644 \u0627\u0644\u0637\u0644\u0628\u0627\u062a"
                        : "Could not load orders"
                )
            );
        } finally {
            setLoading(false);
        }
    }, [lang]);


    useEffect(() => {
        load();
    }, [load]);


    const statusLabel = (status, order = null) => {
        if (
            order
            && (
                order.payment_status === "refunded"
                || order.refund_status === "refunded"
            )
        ) {
            return lang === "ar"
                ? "مسترجع"
                : "Refunded";
        }

        const labels = {
            pending_payment:
                lang === "ar"
                    ? "\u0628\u0627\u0646\u062a\u0638\u0627\u0631 \u0627\u0644\u062f\u0641\u0639"
                    : "Pending payment",

            confirmed:
                lang === "ar"
                    ? "\u0645\u0624\u0643\u062f"
                    : "Confirmed",

            accepted:
                lang === "ar"
                    ? "\u062a\u0645 \u0642\u0628\u0648\u0644 \u0627\u0644\u0637\u0644\u0628"
                    : "Accepted",

            preparing:
                lang === "ar"
                    ? "\u0642\u064a\u062f \u0627\u0644\u062a\u062c\u0647\u064a\u0632"
                    : "Preparing",

            shipped:
                lang === "ar"
                    ? "\u062a\u0645 \u0627\u0644\u0634\u062d\u0646"
                    : "Shipped",

            delivered:
                lang === "ar"
                    ? "\u062a\u0645 \u0627\u0644\u062a\u0648\u0635\u064a\u0644"
                    : "Delivered",

            rejected:
                lang === "ar"
                    ? "\u0645\u0631\u0641\u0648\u0636"
                    : "Rejected",

            cancelled:
                lang === "ar"
                    ? "\u0645\u0644\u063a\u064a"
                    : "Cancelled",
        };

        return labels[status]
            || status
            || "-";
    };


    const paymentLabel = (status) => {
        const labels = {
            paid:
                lang === "ar"
                    ? "\u0645\u062f\u0641\u0648\u0639"
                    : "Paid",

            pending:
                lang === "ar"
                    ? "\u0628\u0627\u0646\u062a\u0638\u0627\u0631 \u0627\u0644\u062f\u0641\u0639"
                    : "Payment pending",

            failed:
                lang === "ar"
                    ? "\u0641\u0634\u0644 \u0627\u0644\u062f\u0641\u0639"
                    : "Payment failed",

            cancelled:
                lang === "ar"
                    ? "\u0645\u0644\u063a\u064a"
                    : "Cancelled",

            refunded:
                lang === "ar"
                    ? "\u062a\u0645 \u0627\u0633\u062a\u0631\u062c\u0627\u0639 \u0627\u0644\u0645\u0628\u0644\u063a"
                    : "Refunded",
        };

        return labels[status]
            || status
            || "-";
    };


    const canRefund = (order) => {
        return (
            order.payment_method === "moyasar"
            && order.payment_status === "paid"
            && refundableStatuses.has(
                order.status
            )
            && ![
                "processing",
                "refunded",
                "unknown",
                "manual_review",
            ].includes(
                order.refund_status
            )
        );
    };


    const refundOrder = async (order) => {
        const confirmed =
            window.confirm(
                lang === "ar"
                    ? "\u0647\u0644 \u0623\u0646\u062a \u0645\u062a\u0623\u0643\u062f \u0645\u0646 \u0627\u0633\u062a\u0631\u062c\u0627\u0639 \u0643\u0627\u0645\u0644 \u0645\u0628\u0644\u063a \u0647\u0630\u0627 \u0627\u0644\u0637\u0644\u0628\u061f\n\n\u0633\u064a\u062a\u0645 \u0625\u0644\u063a\u0627\u0621 \u0627\u0644\u0637\u0644\u0628 \u0648\u0625\u0639\u0627\u062f\u0629 \u0627\u0644\u0645\u062e\u0632\u0648\u0646 \u0639\u0646\u062f \u0646\u062c\u0627\u062d \u0627\u0644\u0627\u0633\u062a\u0631\u062c\u0627\u0639."
                    : "Refund the full amount for this order?\n\nThe order will be cancelled and stock restored after a successful refund."
            );

        if (!confirmed) {
            return;
        }

        setRefundingId(
            order.order_id
        );

        try {
            const response =
                await api.post(
                    `/admin/orders/${order.order_id}/refund`
                );

            toast.success(
                response.data
                    ?.duplicate
                    ? (
                        lang === "ar"
                            ? "\u0627\u0644\u0637\u0644\u0628 \u0645\u0633\u062a\u0631\u062c\u0639 \u0645\u0633\u0628\u0642\u0627\u064b"
                            : "Order was already refunded"
                    )
                    : (
                        lang === "ar"
                            ? "\u062a\u0645 \u0627\u0633\u062a\u0631\u062c\u0627\u0639 \u0627\u0644\u0645\u0628\u0644\u063a \u0628\u0646\u062c\u0627\u062d"
                            : "Refund completed successfully"
                    )
            );

            await load();

        } catch (error) {
            toast.error(
                formatApiError(
                    error.response
                        ?.data
                        ?.detail
                )
                || (
                    lang === "ar"
                        ? "\u062a\u0639\u0630\u0631 \u0627\u0633\u062a\u0631\u062c\u0627\u0639 \u0627\u0644\u0645\u0628\u0644\u063a"
                        : "Refund failed"
                )
            );

        } finally {
            setRefundingId(null);
        }
    };


    const money = (order) => {
        const value =
            order.total
            ?? order.grand_total
            ?? order.total_amount
            ?? 0;

        return Number(value)
            .toFixed(2);
    };


    const displayedStatus = (order) => {
        if (
            order.payment_status === "refunded"
            || order.refund_status === "refunded"
        ) {
            return "refunded";
        }

        return order.status || "";
    };


    const filteredOrders = useMemo(() => {
        const query =
            search.trim().toLowerCase();

        return orders.filter((order) => {
            if (
                query
                && !String(
                    order.order_id || ""
                )
                    .toLowerCase()
                    .includes(query)
            ) {
                return false;
            }

            if (
                orderStatusFilter
                && displayedStatus(order)
                    !== orderStatusFilter
            ) {
                return false;
            }

            if (
                paymentStatusFilter
                && order.payment_status
                    !== paymentStatusFilter
            ) {
                return false;
            }

            const amount = Number(
                order.total
                ?? order.grand_total
                ?? order.total_amount
                ?? 0
            );

            if (
                minAmount !== ""
                && amount < Number(minAmount)
            ) {
                return false;
            }

            if (
                maxAmount !== ""
                && amount > Number(maxAmount)
            ) {
                return false;
            }

            if (dateFrom || dateTo) {
                if (!order.created_at) {
                    return false;
                }

                const createdAt =
                    new Date(order.created_at);

                if (
                    Number.isNaN(
                        createdAt.getTime()
                    )
                ) {
                    return false;
                }

                if (dateFrom) {
                    const from =
                        new Date(
                            `${dateFrom}T00:00:00`
                        );

                    if (createdAt < from) {
                        return false;
                    }
                }

                if (dateTo) {
                    const to =
                        new Date(
                            `${dateTo}T23:59:59.999`
                        );

                    if (createdAt > to) {
                        return false;
                    }
                }
            }

            return true;
        });
    }, [
        orders,
        search,
        orderStatusFilter,
        paymentStatusFilter,
        dateFrom,
        dateTo,
        minAmount,
        maxAmount,
    ]);


    const clearFilters = () => {
        setSearch("");
        setOrderStatusFilter("");
        setPaymentStatusFilter("");
        setDateFrom("");
        setDateTo("");
        setMinAmount("");
        setMaxAmount("");
    };


    return (
        <Layout>
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">

                <div className="flex flex-wrap items-center justify-between gap-4">

                    <div>
                        <h1 className="text-3xl md:text-4xl font-black text-espresso">
                            {lang === "ar"
                                ? "\u0625\u062f\u0627\u0631\u0629 \u0627\u0644\u0637\u0644\u0628\u0627\u062a"
                                : "Manage orders"}
                        </h1>

                        <p className="mt-2 text-sm text-muted-foreground">
                            {lang === "ar"
                                ? "\u0645\u0631\u0627\u062c\u0639\u0629 \u0627\u0644\u0637\u0644\u0628\u0627\u062a \u0648\u0627\u0633\u062a\u0631\u062c\u0627\u0639 \u0645\u062f\u0641\u0648\u0639\u0627\u062a \u0645\u064a\u0633\u0631"
                                : "Review orders and refund Moyasar payments"}
                        </p>
                    </div>

                    <Button
                        variant="outline"
                        onClick={load}
                    >
                        {lang === "ar"
                            ? "\u062a\u062d\u062f\u064a\u062b"
                            : "Refresh"}
                    </Button>

                </div>


                <div className="mt-8 bg-white border border-border rounded-2xl p-5">

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">

                        <div className="lg:col-span-2">
                            <label className="text-xs font-bold text-muted-foreground">
                                {lang === "ar"
                                    ? "البحث برقم الطلب"
                                    : "Search order ID"}
                            </label>

                            <Input
                                className="mt-1.5"
                                value={search}
                                onChange={(e) =>
                                    setSearch(
                                        e.target.value
                                    )
                                }
                                placeholder={
                                    lang === "ar"
                                        ? "مثال: 37563 أو order_37563..."
                                        : "Example: 37563 or order_37563..."
                                }
                            />
                        </div>


                        <div>
                            <label className="text-xs font-bold text-muted-foreground">
                                {lang === "ar"
                                    ? "حالة الطلب"
                                    : "Order status"}
                            </label>

                            <select
                                className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                value={
                                    orderStatusFilter
                                }
                                onChange={(e) =>
                                    setOrderStatusFilter(
                                        e.target.value
                                    )
                                }
                            >
                                <option value="">
                                    {lang === "ar"
                                        ? "كل الحالات"
                                        : "All statuses"}
                                </option>

                                <option value="pending_payment">
                                    {lang === "ar"
                                        ? "بانتظار الدفع"
                                        : "Pending payment"}
                                </option>

                                <option value="confirmed">
                                    {lang === "ar"
                                        ? "مؤكد"
                                        : "Confirmed"}
                                </option>

                                <option value="accepted">
                                    {lang === "ar"
                                        ? "تم قبول الطلب"
                                        : "Accepted"}
                                </option>

                                <option value="preparing">
                                    {lang === "ar"
                                        ? "قيد التجهيز"
                                        : "Preparing"}
                                </option>

                                <option value="shipped">
                                    {lang === "ar"
                                        ? "تم الشحن"
                                        : "Shipped"}
                                </option>

                                <option value="delivered">
                                    {lang === "ar"
                                        ? "تم التوصيل"
                                        : "Delivered"}
                                </option>

                                <option value="refunded">
                                    {lang === "ar"
                                        ? "مسترجع"
                                        : "Refunded"}
                                </option>

                                <option value="cancelled">
                                    {lang === "ar"
                                        ? "ملغي"
                                        : "Cancelled"}
                                </option>

                                <option value="rejected">
                                    {lang === "ar"
                                        ? "مرفوض"
                                        : "Rejected"}
                                </option>
                            </select>
                        </div>


                        <div>
                            <label className="text-xs font-bold text-muted-foreground">
                                {lang === "ar"
                                    ? "حالة الدفع"
                                    : "Payment status"}
                            </label>

                            <select
                                className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                value={
                                    paymentStatusFilter
                                }
                                onChange={(e) =>
                                    setPaymentStatusFilter(
                                        e.target.value
                                    )
                                }
                            >
                                <option value="">
                                    {lang === "ar"
                                        ? "كل حالات الدفع"
                                        : "All payment statuses"}
                                </option>

                                <option value="paid">
                                    {lang === "ar"
                                        ? "مدفوع"
                                        : "Paid"}
                                </option>

                                <option value="refunded">
                                    {lang === "ar"
                                        ? "تم استرجاع المبلغ"
                                        : "Refunded"}
                                </option>

                                <option value="pending">
                                    {lang === "ar"
                                        ? "بانتظار الدفع"
                                        : "Payment pending"}
                                </option>

                                <option value="failed">
                                    {lang === "ar"
                                        ? "فشل الدفع"
                                        : "Payment failed"}
                                </option>

                                <option value="cancelled">
                                    {lang === "ar"
                                        ? "ملغي"
                                        : "Cancelled"}
                                </option>
                            </select>
                        </div>


                        <div>
                            <label className="text-xs font-bold text-muted-foreground">
                                {lang === "ar"
                                    ? "من تاريخ"
                                    : "From date"}
                            </label>

                            <Input
                                type="date"
                                className="mt-1.5"
                                value={dateFrom}
                                onChange={(e) =>
                                    setDateFrom(
                                        e.target.value
                                    )
                                }
                            />
                        </div>


                        <div>
                            <label className="text-xs font-bold text-muted-foreground">
                                {lang === "ar"
                                    ? "إلى تاريخ"
                                    : "To date"}
                            </label>

                            <Input
                                type="date"
                                className="mt-1.5"
                                value={dateTo}
                                onChange={(e) =>
                                    setDateTo(
                                        e.target.value
                                    )
                                }
                            />
                        </div>


                        <div>
                            <label className="text-xs font-bold text-muted-foreground">
                                {lang === "ar"
                                    ? "أقل مبلغ"
                                    : "Minimum amount"}
                            </label>

                            <Input
                                type="number"
                                min="0"
                                step="0.01"
                                className="mt-1.5"
                                value={minAmount}
                                onChange={(e) =>
                                    setMinAmount(
                                        e.target.value
                                    )
                                }
                                placeholder="0.00"
                            />
                        </div>


                        <div>
                            <label className="text-xs font-bold text-muted-foreground">
                                {lang === "ar"
                                    ? "أعلى مبلغ"
                                    : "Maximum amount"}
                            </label>

                            <Input
                                type="number"
                                min="0"
                                step="0.01"
                                className="mt-1.5"
                                value={maxAmount}
                                onChange={(e) =>
                                    setMaxAmount(
                                        e.target.value
                                    )
                                }
                                placeholder="0.00"
                            />
                        </div>

                    </div>


                    <div className="mt-5 flex flex-wrap items-center justify-between gap-3">

                        <div className="text-sm text-muted-foreground">
                            {lang === "ar"
                                ? `عدد النتائج: ${filteredOrders.length} من أصل ${orders.length} طلب`
                                : `${filteredOrders.length} of ${orders.length} orders`}
                        </div>

                        <Button
                            variant="outline"
                            onClick={clearFilters}
                        >
                            {lang === "ar"
                                ? "مسح الفلاتر"
                                : "Clear filters"}
                        </Button>

                    </div>

                </div>


                <div className="mt-5 bg-white border border-border rounded-2xl overflow-hidden">

                    {loading ? (

                        <div className="p-10 text-center text-muted-foreground">
                            ...
                        </div>

                    ) : orders.length === 0 ? (

                        <div className="p-10 text-center text-muted-foreground">
                            {lang === "ar"
                                ? "\u0644\u0627 \u062a\u0648\u062c\u062f \u0637\u0644\u0628\u0627\u062a"
                                : "No orders"}
                        </div>

                    ) : filteredOrders.length === 0 ? (

                        <div className="p-10 text-center text-muted-foreground">
                            {lang === "ar"
                                ? "لا توجد طلبات مطابقة للفلاتر"
                                : "No orders match the selected filters"}
                        </div>

                    ) : (

                        <div className="overflow-x-auto">

                            <table className="w-full text-sm">

                                <thead className="bg-surface text-muted-foreground text-xs">

                                    <tr>
                                        <th className="text-start p-4">
                                            {lang === "ar"
                                                ? "\u0627\u0644\u0637\u0644\u0628"
                                                : "Order"}
                                        </th>

                                        <th className="text-start p-4">
                                            {lang === "ar"
                                                ? "\u0627\u0644\u0645\u0628\u0644\u063a"
                                                : "Amount"}
                                        </th>

                                        <th className="text-start p-4">
                                            {lang === "ar"
                                                ? "\u062d\u0627\u0644\u0629 \u0627\u0644\u0637\u0644\u0628"
                                                : "Order status"}
                                        </th>

                                        <th className="text-start p-4">
                                            {lang === "ar"
                                                ? "\u062d\u0627\u0644\u0629 \u0627\u0644\u062f\u0641\u0639"
                                                : "Payment"}
                                        </th>

                                        <th className="text-start p-4">
                                            {lang === "ar"
                                                ? "\u0627\u0644\u0627\u0633\u062a\u0631\u062c\u0627\u0639"
                                                : "Refund"}
                                        </th>
                                    </tr>

                                </thead>


                                <tbody>

                                    {filteredOrders.map(
                                        (order) => (

                                            <tr
                                                key={order.order_id}
                                                className="border-t border-border"
                                            >

                                                <td className="p-4">
                                                    <div className="font-mono text-xs">
                                                        {order.order_id}
                                                    </div>

                                                    <div className="text-xs text-muted-foreground mt-1">
                                                        {order.created_at
                                                            ? new Date(
                                                                order.created_at
                                                            ).toLocaleString()
                                                            : ""}
                                                    </div>
                                                </td>


                                                <td className="p-4 font-bold text-terracotta whitespace-nowrap">
                                                    SAR {money(order)}
                                                </td>


                                                <td className="p-4">
                                                    <span className="inline-flex rounded-full bg-surface px-3 py-1 font-bold">
                                                        {statusLabel(
                                                            order.status,
                                                            order
                                                        )}
                                                    </span>
                                                </td>


                                                <td className="p-4">
                                                    <span
                                                        className={`inline-flex rounded-full px-3 py-1 font-bold ${
                                                            order.payment_status === "paid"
                                                                ? "bg-green-100 text-green-700"
                                                                : order.payment_status === "refunded"
                                                                    ? "bg-amber-100 text-amber-700"
                                                                    : order.payment_status === "failed"
                                                                        ? "bg-red-100 text-red-700"
                                                                        : "bg-surface"
                                                        }`}
                                                    >
                                                        {paymentLabel(
                                                            order.payment_status
                                                        )}
                                                    </span>
                                                </td>


                                                <td className="p-4">

                                                    {canRefund(order) ? (

                                                        <Button
                                                            className="rounded-full bg-terracotta text-white"
                                                            disabled={
                                                                refundingId
                                                                === order.order_id
                                                            }
                                                            onClick={() =>
                                                                refundOrder(
                                                                    order
                                                                )
                                                            }
                                                        >
                                                            {refundingId
                                                                === order.order_id
                                                                ? (
                                                                    lang === "ar"
                                                                        ? "\u062c\u0627\u0631\u064a \u0627\u0644\u0627\u0633\u062a\u0631\u062c\u0627\u0639..."
                                                                        : "Refunding..."
                                                                )
                                                                : (
                                                                    lang === "ar"
                                                                        ? "\u0627\u0633\u062a\u0631\u062c\u0627\u0639 \u0627\u0644\u0645\u0628\u0644\u063a"
                                                                        : "Refund"
                                                                )}
                                                        </Button>

                                                    ) : order.payment_status
                                                        === "refunded" ? (

                                                        <span className="font-bold text-amber-700">
                                                            {lang === "ar"
                                                                ? "\u062a\u0645 \u0627\u0644\u0627\u0633\u062a\u0631\u062c\u0627\u0639"
                                                                : "Refunded"}
                                                        </span>

                                                    ) : (

                                                        <span className="text-muted-foreground">
                                                            -
                                                        </span>
                                                    )}

                                                </td>

                                            </tr>
                                        )
                                    )}

                                </tbody>

                            </table>

                        </div>
                    )}

                </div>

            </div>
        </Layout>
    );
}
